/**
 * DashboardTab.js - Tab de Dashboard con métricas principales
 *
 * IMPORTANTE: Solo se cuentan tags IMPRESOS (estadoImpresion === 'impreso')
 * Los tags no impresos no existen físicamente y no se contabilizan.
 *
 * Métricas:
 * - inventarioFisico: estadoImpresion === 'impreso' && vendido === false
 * - stockDisponible: estadoImpresion === 'impreso' && liberado === true && vendido === false
 * - vendidos: vendido === true (ya entregados al cliente)
 * - activados: estado !== 'inactivo' (tags en uso)
 * - pendientesActivar: vendido === true && estado === 'inactivo'
 */
import React from "react";

function DashboardTab({ metricas, loading, onRefresh }) {
  if (loading && !metricas) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">Cargando métricas...</span>
        </div>
        <p className="mt-3 text-muted">Cargando métricas...</p>
      </div>
    );
  }

  if (!metricas) {
    return (
      <div className="text-center py-5">
        <i className="bi bi-exclamation-circle display-4 text-muted"></i>
        <p className="mt-3 text-muted">No hay datos disponibles</p>
        <button className="btn btn-outline-primary" onClick={onRefresh}>
          Intentar de nuevo
        </button>
      </div>
    );
  }

  const {
    inventarioFisico = 0,
    stockDisponible = 0,
    vendidos = 0,
    activados = 0,
    tasaConversion = 0,
    vendidosMes = 0,
    pendientesActivar = 0,
    // Retrocompatibilidad (eslint-disable-next-line no-unused-vars)
    // eslint-disable-next-line no-unused-vars
    enStock = stockDisponible
  } = metricas;

  return (
    <div className="dashboard-tab">
      {/* Métricas principales - Inventario */}
      <div className="row g-4 mb-4">
        {/* Inventario Físico Total (solo impresos) */}
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card metric-card h-100">
            <div className="card-body">
              <div className="d-flex align-items-start justify-content-between">
                <div>
                  <p className="metric-label mb-1">Inventario Físico</p>
                  <p className="metric-value text-secondary mb-0">{inventarioFisico}</p>
                  <p className="metric-sub">Tags impresos en stock</p>
                </div>
                <div className="metric-icon bg-secondary bg-opacity-10 text-secondary">
                  <i className="bi bi-boxes"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stock Disponible (Listos para enviar) */}
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card metric-card h-100">
            <div className="card-body">
              <div className="d-flex align-items-start justify-content-between">
                <div>
                  <p className="metric-label mb-1">Listos para Enviar</p>
                  <p className="metric-value text-primary mb-0">{stockDisponible}</p>
                  <p className="metric-sub">Liberados, sin vender</p>
                </div>
                <div className="metric-icon bg-primary bg-opacity-10 text-primary">
                  <i className="bi bi-box-seam"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Vendidos (Entregados) */}
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card metric-card h-100">
            <div className="card-body">
              <div className="d-flex align-items-start justify-content-between">
                <div>
                  <p className="metric-label mb-1">Entregados</p>
                  <p className="metric-value text-success mb-0">{vendidos}</p>
                  <p className="metric-sub">
                    <span className="text-success">
                      <i className="bi bi-arrow-up"></i> {vendidosMes}
                    </span>
                    {' '}este mes
                  </p>
                </div>
                <div className="metric-icon bg-success bg-opacity-10 text-success">
                  <i className="bi bi-truck"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Métricas de Conversión */}
      <div className="row g-4 mb-4">
        {/* Activados */}
        <div className="col-12 col-sm-6 col-lg-4">
          <div className="card metric-card h-100">
            <div className="card-body">
              <div className="d-flex align-items-start justify-content-between">
                <div>
                  <p className="metric-label mb-1">Activados</p>
                  <p className="metric-value text-info mb-0">{activados}</p>
                  <p className="metric-sub">Tags en uso por clientes</p>
                </div>
                <div className="metric-icon bg-info bg-opacity-10 text-info">
                  <i className="bi bi-check-circle"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pendientes de Activar */}
        <div className="col-12 col-sm-6 col-lg-4">
          <div className="card metric-card h-100">
            <div className="card-body">
              <div className="d-flex align-items-start justify-content-between">
                <div>
                  <p className="metric-label mb-1">Pendientes Activar</p>
                  <p className="metric-value mb-0" style={{ color: pendientesActivar > 0 ? '#ffc107' : '#198754' }}>
                    {pendientesActivar}
                  </p>
                  <p className="metric-sub">Entregados sin activar</p>
                </div>
                <div className="metric-icon" style={{
                  backgroundColor: pendientesActivar > 0 ? 'rgba(255,193,7,0.1)' : 'rgba(25,135,84,0.1)',
                  color: pendientesActivar > 0 ? '#ffc107' : '#198754'
                }}>
                  <i className="bi bi-hourglass-split"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tasa de Conversión */}
        <div className="col-12 col-sm-6 col-lg-4">
          <div className="card metric-card h-100">
            <div className="card-body">
              <div className="d-flex align-items-start justify-content-between">
                <div>
                  <p className="metric-label mb-1">Tasa de Conversión</p>
                  <p className="metric-value mb-0" style={{ color: tasaConversion >= 90 ? '#198754' : tasaConversion >= 70 ? '#ffc107' : '#dc3545' }}>
                    {tasaConversion}%
                  </p>
                  <p className="metric-sub">Activados / Entregados</p>
                </div>
                <div className="metric-icon" style={{
                  backgroundColor: `${tasaConversion >= 90 ? 'rgba(25,135,84,0.1)' : tasaConversion >= 70 ? 'rgba(255,193,7,0.1)' : 'rgba(220,53,69,0.1)'}`,
                  color: tasaConversion >= 90 ? '#198754' : tasaConversion >= 70 ? '#ffc107' : '#dc3545'
                }}>
                  <i className="bi bi-graph-up"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Funnel de conversión visual */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-white">
              <h6 className="mb-0">
                <i className="bi bi-funnel me-2"></i>
                Funnel de Conversión
              </h6>
            </div>
            <div className="card-body">
              <div className="conversion-funnel">
                <div className="funnel-step">
                  <div className="funnel-box">
                    <div className="funnel-value">{vendidos}</div>
                    <div className="funnel-label">Entregados</div>
                  </div>
                  <div className="funnel-arrow">
                    <i className="bi bi-arrow-right"></i>
                  </div>
                  <div className="funnel-box">
                    <div className="funnel-value">{activados}</div>
                    <div className="funnel-label">Activados</div>
                  </div>
                  <div className="funnel-arrow">
                    <i className="bi bi-arrow-right"></i>
                  </div>
                  <div className="funnel-box" style={{
                    borderColor: pendientesActivar > 0 ? '#ffc107' : '#198754',
                    borderWidth: '2px',
                    borderStyle: 'solid'
                  }}>
                    <div className="funnel-value" style={{ color: pendientesActivar > 0 ? '#ffc107' : '#198754' }}>
                      {pendientesActivar}
                    </div>
                    <div className="funnel-label">Pendientes</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Distribución de stock por Tipo + Plan */}
      {metricas.stockPorTipoPlan && Object.keys(metricas.stockPorTipoPlan).length > 0 && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header bg-white">
                <h6 className="mb-0">
                  <i className="bi bi-grid-3x3 me-2"></i>
                  Stock por Tipo y Plan
                </h6>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Tipo</th>
                        <th>Plan</th>
                        <th className="text-center">Cantidad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Orden fijo de tipos y planes
                        const tipoOrder = { mascota: 1, llavero: 2, objeto: 2, coleccionable: 3 };
                        const planOrder = { premium: 1, standard: 2, basico: 3, light: 4 };

                        // Ordenar entradas
                        const sortedEntries = Object.entries(metricas.stockPorTipoPlan).sort((a, b) => {
                          const [tipoA, planA] = a[0].split('_');
                          const [tipoB, planB] = b[0].split('_');

                          // Primero ordenar por tipo
                          const tipoCompare = (tipoOrder[tipoA] || 99) - (tipoOrder[tipoB] || 99);
                          if (tipoCompare !== 0) return tipoCompare;

                          // Luego por plan
                          return (planOrder[planA] || 99) - (planOrder[planB] || 99);
                        });

                        return sortedEntries.map(([key, cantidad]) => {
                          const [tipo, plan] = key.split('_');
                          return (
                            <tr key={key}>
                              <td>
                                <i className={`bi ${tipo === 'mascota' ? 'bi-heart' : tipo === 'llavero' || tipo === 'objeto' ? 'bi-key' : 'bi-gem'} me-2 text-muted`}></i>
                                {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
                              </td>
                              <td>
                                <span className={`badge ${plan === 'premium' ? 'bg-warning text-dark' : plan === 'standard' ? 'bg-info' : plan === 'basico' ? 'bg-success' : 'bg-secondary'}`}>
                                  {plan.charAt(0).toUpperCase() + plan.slice(1)}
                                </span>
                              </td>
                              <td className="text-center">
                                <span className="badge bg-primary rounded-pill">{cantidad}</span>
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardTab;
